import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { PdfDocumentRemoteControllerService } from "core/api";
import { IPdfDocumentInput, PdfDocumentViewportsContainerService, PdfViewerService } from "core/pdf-new";
import { subjectFromObs } from "core/rx-js";
import { ScreenUtilsService } from "core/services";
import { Subject, distinctUntilChanged, map, switchMap, take, takeUntil } from "rxjs";
import { ITask } from "../../dto-map/i-task";
import { SignProcessWizardService } from "../../sign-process-wizard.service";
import { SelectTasksStepService } from "./select-tasks-step.service";
import { TaskCollectionService } from "./task-collection.service";


@Component({
  selector: 'ss-select-tasks-step',
  templateUrl: './select-tasks-step.component.html',
  styleUrls: ['./select-tasks-step.component.css'],
  providers: [
    TaskCollectionService,
    PdfViewerService,
    PdfDocumentViewportsContainerService,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectTasksStepComponent implements OnInit {
  private readonly _destroy = new Subject<void>();

  largeScreenHeight = 'calc(100dvh - 26rem)';
  smallScreenHeight = 'calc(100dvh - 10rem)'

  get screenSize() {
    return this._screenUtilsService.screenSizeSubject.value
  }

  readonly calculatedHeightObs = this._screenUtilsService.screenSizeSubject.pipe(
    map(() => ['xl', 'lg'].includes(this.screenSize) ? this.largeScreenHeight : this.smallScreenHeight)
  )

  readonly showSignaturesSubject = subjectFromObs({
    initialValue: false,
    observable: this._viewerService.pagesLoadedObs.pipe(
      map(Boolean),
      distinctUntilChanged()
    ),
    until: this._destroy
  })

  @ViewChild(TemplateRef<any>)
  private _templateRef!: TemplateRef<any>

  readonly tasksSubject = this._selectTasksStepService.tasksSubject;
  readonly heightObs = this._pdfDocumentViewportsContainerService.totalHeightSubject;


  readonly signaturesObs = this._selectTasksStepService.signaturesObs;
  readonly processIdSubject = this._signProcessWizardService.processIdSubject;
  get processId() {
    return this.processIdSubject.value;
  }

  readonly signaturePositionsSubject = this._taskCollectionService.signaturePositionsSubject;

  get signaturePositions() {
    return this.signaturePositionsSubject.value
  }


  readonly scrollTopSubject = this._viewerService.scrollTopSubject;


  get baseElement() {
    return this._signProcessWizardService.baseElement
  }

  get otherElements() {
    return this._signProcessWizardService.otherElements
  }


  constructor(
    private _signProcessWizardService: SignProcessWizardService,
    private _pdfDocumentViewportsContainerService: PdfDocumentViewportsContainerService,
    private _viewerService: PdfViewerService,
    private _selectTasksStepService: SelectTasksStepService,
    private readonly _pdfDocumentRemoteControllerService: PdfDocumentRemoteControllerService,
    private readonly _screenUtilsService: ScreenUtilsService,
    private readonly _taskCollectionService: TaskCollectionService
  ) {


    this._signProcessWizardService.pdfDocumentsSubject
      .pipe(
        takeUntil(this._destroy),
        switchMap(() => this._pdfDocumentRemoteControllerService.getByProcessWithMetadata(this._signProcessWizardService.processIdSubject.value).pipe(take(1))),
        map((pdfDocuments) => pdfDocuments.map<IPdfDocumentInput>((pdfDoc, idx) => ({
          shortDisplay: pdfDoc.name,
          longDisplay: pdfDoc.name,
          orderIndex: pdfDoc.orderIndex,
          processId: this.processId,
          documentMetadata: pdfDoc.documentMetadata,
          id: pdfDoc.id!
        }))),
      )
      .subscribe(pdfDocuments => this._viewerService.pdfDocumentInputsSubject.next(pdfDocuments))

    const pageLoadFn = (batchNo: number, batchSize: number) => {

      return this._pdfDocumentRemoteControllerService.getImagesByProcessId(this.processId, batchSize, batchNo * batchSize)
        .pipe(
          map((docImages) => docImages.flatMap(docImage => docImage.image))
        )
    }

    this._viewerService.pageLoadFnSubject.next(pageLoadFn);




  }

  updateHeight(height: string) {

    this._pdfDocumentViewportsContainerService.viewportHeightSubject.next(height)
  }

  ngOnInit(): void {

  }

  trackSignatureById(idx: number, signature: ITask<'SIGNATURE'>) {
    return 'signature-' + signature.id
  }

  ngAfterViewInit() {
    this._viewerService.addedPageContentSubject.next(this._templateRef);
  }

  positionChanged(position: { top: number, left: number, height: number, width: number }, signature: ITask) {

    this.signaturePositionsSubject.next({
      ...this.signaturePositions,
      [signature.id]: {
        ...position,
        activityId: signature.activityId
      }
    })

  }

  ngOnDestroy() {
    this._destroy.next();
    this._destroy.complete();
  }
}
